import React from 'react';
import { useTranslation } from 'react-i18next';
import { PURPOSE_KINDS, PurposeChips, TagChips,
         purposeCountKey } from './EventTags';
import { eventDayKey } from '../../utils';
import './LabFilters.css';

/* Sifting the full calendar — the shared body of /member-home/upcoming and /member-home/past.

   One module rather than a copy on each page, because a filter is a state shape, a match rule
   and a row of controls that have to agree with each other; two copies of that is two chances
   for "Events I'm in" to mean something different depending on which list you opened.

   ★ Two tiers of control, and the difference is deliberate:

     ALWAYS — am I in it, when is it, who is running it. Every calendar can answer those three,
     so they are always offered.

     ONLY WHEN THE EVENTS SAY SO — the category, the league, the programme it hangs under, and
     what the event is FOR (theme, value, goal). Those are set on some events and on most of
     them not at all (a Lab hangs under no programme whatsoever), and a chip row that finds
     nothing is a promise the list cannot keep. So each of those rows is drawn from the
     catalogue but narrowed to what the events IN FRONT OF YOU actually carry, and a row with
     nothing behind it does not draw.

   ★ Filtered in the browser, not re-fetched. The page already holds the whole calendar (it is
     the same /api/bravos/home payload the home reads), so a round trip per chip would only make
     the chips feel slow.

   ★ The counts are tallied over the WHOLE list, never over the filtered one, so the numbers on
     the chips stay put while you click them. */

const key = (v) => String(v || '').toLowerCase();

/* Attendance is two chips over one flag, so it is keyed like everything else here. */
const IN = 'in';
const OUT = 'out';

export const emptyLabFilter = () => ({
  attending: [],
  from: '',
  to: '',
  hosts: [],
  tags: [],
  leagues: [],
  programmes: [],
  purpose: { theme: [], value: [], sdg: [] },
});

export const labFilterIsOn = (f) => !!f && (
  f.attending.length > 0 || !!f.from || !!f.to || f.hosts.length > 0
  || f.tags.length > 0 || f.leagues.length > 0 || f.programmes.length > 0
  || PURPOSE_KINDS.some((kind) => (f.purpose[kind] || []).length > 0)
);

/* The days an event covers, as comparable 'YYYY-MM-DD' keys — read in the event's own zone, so
   the range you type matches the dates the row prints (see `eventDayKey`). A one-day event is
   the same key twice; an event with no date at all covers nothing and is only ever dropped by a
   date range, never by the other rows. */
const dayRange = (e) => {
  const start = eventDayKey(e);
  if (!start) return null;
  const end = eventDayKey({ ...e, date: e.end_date }) || start;
  return end < start ? [end, start] : [start, end];
};

/* ANY of the picked chips within a row ("show me these kinds"), ALL of the rows across them
   (a category AND a host AND a theme) — the same rule the programme calendar filters by, because
   a filter that found MORE as you added to it would answer nobody's question. */
export const matchesLabFilter = (e, f) => {
  if (!f) return true;
  if (f.attending.length > 0
      && !f.attending.includes(e.attending ? IN : OUT)) return false;
  if (f.from || f.to) {
    const range = dayRange(e);
    // An event overlapping the window is in it — a two-day Lab found by its second day is the
    // same Lab. No date at all cannot overlap anything.
    if (!range) return false;
    if (f.from && range[1] < f.from) return false;
    if (f.to && range[0] > f.to) return false;
  }
  if (f.hosts.length > 0
      && !(e.hosts || []).some((h) => f.hosts.includes(key(h.id)))) return false;
  if (f.tags.length > 0
      && !(e.tags || []).some((tg) => f.tags.includes(key(tg.id)))) return false;
  if (f.leagues.length > 0
      && !(e.league && f.leagues.includes(key(e.league.id)))) return false;
  if (f.programmes.length > 0
      && !(e.programme && f.programmes.includes(key(e.programme.id)))) return false;
  const has = new Set((e.purpose || []).map((it) => purposeCountKey(it.kind, it.id)));
  return PURPOSE_KINDS.every((kind) => {
    const picked = f.purpose[kind] || [];
    return picked.length === 0 || picked.some((id) => has.has(purposeCountKey(kind, id)));
  });
};

/* Is a chip row worth drawing? Only when it can actually SPLIT the list. More than one chip
   always can; a single chip can only when something is left outside it — one host who runs every
   Lab hides nothing, one host among Labs that name nobody hides plenty. */
const canSplit = (chips, counts, total) => (
  chips.length > 1
  || (chips.length === 1 && (counts[key(chips[0].id)] || 0) < total)
);

/* Chips for something the events carry rather than a catalogue: the hosts and the leagues. Both
   arrive on the rows with an id and a name, so the list is whatever the calendar says it is —
   busiest first, then by name so the order cannot wobble between two equally busy ones. */
const fromRows = (events, pick) => {
  const counts = {};
  const names = {};
  events.forEach((e) => (pick(e) || []).forEach((row) => {
    if (!row || !row.id) return;
    const k = key(row.id);
    counts[k] = (counts[k] || 0) + 1;
    if (!names[k]) names[k] = row.name || '';
  }));
  const rows = Object.keys(counts)
    .sort((a, b) => counts[b] - counts[a] || names[a].localeCompare(names[b]))
    .map((k) => ({ id: k, name: names[k] }));
  return { rows, counts };
};

const LabFilters = ({ events, tagCatalog, purposeCatalog, value, onChange, past = false }) => {
  const { t } = useTranslation();
  const rows = events || [];
  const f = value;

  // How many events each chip is worth, over the whole list.
  const attendCounts = {
    [IN]: rows.filter((e) => e.attending).length,
    [OUT]: rows.filter((e) => !e.attending).length,
  };
  const tagCounts = {};
  rows.forEach((e) => (e.tags || []).forEach((tg) => {
    const k = key(tg.id);
    tagCounts[k] = (tagCounts[k] || 0) + 1;
  }));
  const purposeCounts = {};
  rows.forEach((e) => (e.purpose || []).forEach((it) => {
    const k = purposeCountKey(it.kind, it.id);
    purposeCounts[k] = (purposeCounts[k] || 0) + 1;
  }));
  const hosts = fromRows(rows, (e) => e.hosts);
  const leagues = fromRows(rows, (e) => (e.league ? [e.league] : []));
  const programmes = fromRows(rows, (e) => (e.programme ? [e.programme] : []));
  // Only the categories these events actually carry. The catalogue is what gives them their
  // order and their colour; the counts are what decide whether a chip is drawn at all.
  const tags = (tagCatalog || []).filter((tg) => tagCounts[key(tg.id)]);

  // "In" and "not in" are one flag read two ways. On a list of things that already happened
  // that flag is a memory rather than a plan, so the two chips say so.
  const attendance = [
    { id: IN, name: past ? t('filterAttended') : t('filterAttending') },
    { id: OUT, name: past ? t('filterNotAttended') : t('filterNotAttending') },
  ];

  const toggle = (field, id) => onChange({
    ...f,
    [field]: (f[field].includes(key(id))
      ? f[field].filter((x) => x !== key(id))
      : [...f[field], key(id)]),
  });
  const togglePurpose = (kind, id) => onChange({
    ...f,
    purpose: {
      ...f.purpose,
      [kind]: ((f.purpose[kind] || []).includes(key(id))
        ? f.purpose[kind].filter((x) => x !== key(id))
        : [...(f.purpose[kind] || []), key(id)]),
    },
  });

  return (
    <div className="labfil">
      <TagChips tags={attendance} selected={f.attending} counts={attendCounts}
                onToggle={(id) => toggle('attending', id)} label={t('filterAttendance')} small />

      {/* From and to, both optional: one on its own is "everything after this" / "everything
          up to this", which is how a calendar is usually read. */}
      <span className="etags__label">{t('dateRange')}</span>
      <div className="labfil__dates">
        <label className="labfil__date">
          <span>{t('from')}</span>
          <input
            type="date"
            value={f.from}
            max={f.to || undefined}
            onChange={(ev) => onChange({ ...f, from: ev.target.value })}
          />
        </label>
        <label className="labfil__date">
          <span>{t('to')}</span>
          <input
            type="date"
            value={f.to}
            min={f.from || undefined}
            onChange={(ev) => onChange({ ...f, to: ev.target.value })}
          />
        </label>
      </div>

      {/* Who is running it. Nobody is named on a company's own event, so this row simply does
          not draw on a calendar made of those. */}
      {canSplit(hosts.rows, hosts.counts, rows.length) && (
        <TagChips tags={hosts.rows} selected={f.hosts} counts={hosts.counts}
                  onToggle={(id) => toggle('hosts', id)} label={t('hostedBy')} small />
      )}

      {/* --- and, only when the events say so ------------------------------------------- */}
      {canSplit(tags, tagCounts, rows.length) && (
        <TagChips tags={tags} selected={f.tags} counts={tagCounts}
                  onToggle={(id) => toggle('tags', id)} label={t('eventTags')} small />
      )}
      {canSplit(leagues.rows, leagues.counts, rows.length) && (
        <TagChips tags={leagues.rows} selected={f.leagues} counts={leagues.counts}
                  onToggle={(id) => toggle('leagues', id)} label={t('league')} small />
      )}
      {canSplit(programmes.rows, programmes.counts, rows.length) && (
        <TagChips tags={programmes.rows} selected={f.programmes} counts={programmes.counts}
                  onToggle={(id) => toggle('programmes', id)} label={t('programme')} small />
      )}
      <PurposeChips catalog={purposeCatalog} picks={f.purpose}
                    onToggle={togglePurpose} counts={purposeCounts} />

      {labFilterIsOn(f) && (
        <button type="button" className="bravos-tag__add mt-2"
                onClick={() => onChange(emptyLabFilter())}>
          {t('clearFilter')}
        </button>
      )}
    </div>
  );
};

export default LabFilters;
